import isBoolean from 'lodash/isBoolean';
import isString from 'lodash/isString';
import { v4 as uuidv4 } from 'uuid';

import { ITEM_TYPE_CAR_HIRE, ITEM_TYPE_SERVICE_FEE, ITEM_TYPE_SUBSCRIPTION } from '~/consts/order';
import {
  CAR_HIRE_REASONS,
  COLLECTION_REASONS,
  EXPIRED_ONLY_TOUR_REFUND_ALLOWED_TYPES,
  EXPIRED_TOUR_REFUND_ALLOWED_TYPES,
  REASONS_WITH_ADDITIONAL_INFO,
  REFUND_V2,
  SERVICE_FEE_ALLOWED_TYPES,
  SUBSCRIPTION_REASONS,
  SUBSCRIPTION_REASONS_V2,
  TNFH_REASON_SOURCE,
  VARIANT_OF_REFUND_ALL,
} from '~/consts/refund';
import type { CollectionReasonKey } from '~/consts/refund';

import { ReservationRate } from '~/services/ReservationService';

import currencyFormatter from '~/utils/currencyFormatter';

import { AccountingMetaPayload } from '../../NewModal';
import {
  AdditionalInfo,
  CollectionReasons,
  EnrichedRefundMetadata,
  RefundMetadata,
  RefundPayload,
  RefundPayloadAccountingMetadata,
  VendorRefundTypes,
} from '../../types';
import { RefundSteps } from '../LPPRefundV2';

import { NightPrices, SurchargeData, SurchargePrices } from './SurchargeDataHook';

export function getItemType(item: App.OrderItem): string {
  if (!item.type) {
    throw new Error(`Item ${item.id} does not have an item type`);
  }
  return item.type;
}

interface ReasonByTypeDict {
  refundTypes: Array<string>;
  reasons: CollectionReasons;
}

export function getReasonsByItemType(
  itemType: string,
  order: any,
  isRefundable: boolean,
  isExpiredTourRefund = false,
  isItemInPaymentSchedule = false,
): ReasonByTypeDict {
  let possibleCollectionReasons = {
    refundTypes: Object.keys(COLLECTION_REASONS),
    reasons: COLLECTION_REASONS,
  };

  if (isExpiredTourRefund) {
    possibleCollectionReasons.refundTypes = Object.keys(COLLECTION_REASONS).filter((key) =>
      EXPIRED_TOUR_REFUND_ALLOWED_TYPES.includes(key),
    );
    return possibleCollectionReasons;
  }
  switch (itemType) {
    case ITEM_TYPE_CAR_HIRE:
      possibleCollectionReasons = {
        refundTypes: Object.keys(CAR_HIRE_REASONS),
        reasons: CAR_HIRE_REASONS,
      };
      break;
    case ITEM_TYPE_SERVICE_FEE:
      possibleCollectionReasons.refundTypes = Object.keys(COLLECTION_REASONS).filter((key) =>
        SERVICE_FEE_ALLOWED_TYPES.includes(key),
      );
      break;
    case ITEM_TYPE_SUBSCRIPTION:
      possibleCollectionReasons = {
        refundTypes: Object.keys(SUBSCRIPTION_REASONS),
        reasons: SUBSCRIPTION_REASONS_V2,
      };
      break;
    default:
      possibleCollectionReasons.refundTypes = Object.keys(COLLECTION_REASONS).filter(
        (key) => !EXPIRED_ONLY_TOUR_REFUND_ALLOWED_TYPES.includes(key),
      );
  }

  const hasAccommodation = !!order.accommodation_items.length || !!order.bedbank_items.length;
  const hasCruise = !!order.cruise_items.length;
  const hasTour = !!order.tour_items.length;

  // possibleCollectionReasons.refundTypes
  if (hasAccommodation || hasCruise || hasTour) {
    //   Change refund reason type when outside cancellation period
    if (!isRefundable) {
      possibleCollectionReasons.reasons['INSURANCE_BREAKAGE'].defaults.source = TNFH_REASON_SOURCE;
    }
  } else {
    possibleCollectionReasons.refundTypes = Object.keys(COLLECTION_REASONS).filter(
      (key) => key === 'INSURANCE_BREAKAGE',
    );
  }

  return possibleCollectionReasons;
}

export interface CostBreakdown {
  packageCost: string;
  numberOfNights: number;
  surcharges: string;
  avgPricePerNight: string;
  total: string;
}

export function buildComment(isTestPurchase, userFullName, refundApprover, commentFallback) {
  let comment = '';

  if (refundApprover) {
    comment += 'Approver: ' + refundApprover + '\n';
  }
  comment += isTestPurchase ? userFullName : commentFallback;

  return comment;
}

export function getDefaultValues(
  refundMetadata: EnrichedRefundMetadata,
  selectedReasonKey: CollectionReasonKey,
  userFullName: string,
  refundApprover: string,
): RefundPayload {
  let additionalInfo: AdditionalInfo = {};
  if (REASONS_WITH_ADDITIONAL_INFO.includes(selectedReasonKey as unknown as string)) {
    additionalInfo = {
      number_of_nights: 0,
      percentage: 100,
      package_nights: refundMetadata.package_nights,
      total_nights: convertStrOrNumToNum(refundMetadata.total_nights),
      extra_nights: convertStrOrNumToNum(refundMetadata.extra_nights),
    };
  }

  const merchantFeeAmount = refundMetadata.merchant_fee_amount ?? 0;
  const cashAmount = refundMetadata.item_metadata.cash_amount ?? 0;
  const surchargeAmount = refundMetadata.surcharge_metadata?.cash_amount ?? 0;
  let amountToRefund = cashAmount + merchantFeeAmount;
  const accountingMetadatum = [];

  const itemAccountingMetadata = {
    source: refundMetadata.item_metadata.source,
    // cash amount is the amount we have for this order after x operations such as promo discount, partial refunds.
    cash_amount: refundMetadata.item_metadata.cash_amount ?? 0,
    // accounting amount is the total amount we received from the customer during purchase.
    accounting_amount: refundMetadata.item_metadata.accounting_amount ?? 0,
    charge_component_key: refundMetadata.item_metadata.charge_component_key,
    refund_fee: refundMetadata.item_metadata.refund_fee,
  };

  accountingMetadatum.push(itemAccountingMetadata);

  const includeSurchargeMetadata = refundMetadata.has_surcharge;

  if (includeSurchargeMetadata) {
    const surchargeAccountingMetadata = {
      source: refundMetadata.surcharge_metadata.source,
      cash_amount: refundMetadata.surcharge_metadata.cash_amount ?? 0,
      accounting_amount: refundMetadata.surcharge_metadata.accounting_amount ?? 0,
      charge_component_key: refundMetadata.surcharge_metadata.charge_component_key,
      refund_fee: refundMetadata.surcharge_metadata.refund_fee,
    };
    accountingMetadatum.push(surchargeAccountingMetadata);
    amountToRefund += surchargeAmount;
  }

  return {
    transaction_key: uuidv4(),
    reason: refundMetadata?.reason ?? '',
    refund_method: refundMetadata?.refund_method ?? '',
    amount: amountToRefund,
    comment: buildComment(
      selectedReasonKey === 'REFUND_TEST_PURCHASE',
      userFullName,
      refundApprover,
      refundMetadata.comment,
    ),
    ticket_id: refundMetadata.ticket_id,
    room_id: refundMetadata?.room_id ?? undefined,
    currency_code: refundMetadata.currency_code,
    mark_cancelled: refundMetadata.mark_cancelled,
    update_vcc_balance: refundMetadata.update_vcc_balance,
    send_refund_notification: convertStrOrBoolToBool(refundMetadata.send_refund_notification),
    send_customer_refund_notification: convertStrOrBoolToBool(refundMetadata.send_customer_refund_notification),
    accounting_metadata: accountingMetadatum,
    additional_info: additionalInfo,
    disable_credits_promo_refund: refundMetadata.disable_credits_promo_refund,
    remaining_deposit_items: refundMetadata.remaining_deposit_items,
    current_deposit_item: refundMetadata.current_deposit_item,
    is_deposit: refundMetadata.is_deposit,
    is_final_deposit_refund: refundMetadata.is_final_deposit_refund,
    is_instalment: refundMetadata.is_instalment,
    remaining_instalment_items: refundMetadata.remaining_instalment_items,
    current_instalment_item: refundMetadata.current_instalment_item,
    is_pending_deferred_payment: refundMetadata.is_pending_deferred_payment,
    remaining_deferred_payment_items: refundMetadata.remaining_deferred_payment_items,
    current_deferred_payment_item: refundMetadata.current_deferred_payment_item,
    merchant_fee_amount: refundMetadata.merchant_fee_amount,
    variant_of_refund: refundMetadata.variant_of_refund,
    is_last_item_in_order: refundMetadata.is_last_item_in_order,
  };
}

export type RefundMethods = 'Back To Original' | 'LE Credits' | 'Manual';
export function mapRefundMethodToLabel(refundMethod: RefundMethods) {
  switch (refundMethod) {
    case 'Back To Original': {
      return 'Default';
    }
    case 'LE Credits': {
      return 'Credits';
    }
    case 'Manual': {
      return 'Offline/Manual';
    }
  }
}

export function convertStrOrBoolToBool(value: boolean | string) {
  if (isBoolean(value)) {
    return value;
  }
  return value === 'true';
}

export function convertStrOrNumToNum(value: string | number, toFixed = 2) {
  let numberValue: number;
  if (isString(value)) {
    numberValue = parseFloat(value);
  } else {
    numberValue = value;
  }

  return parseFloat(numberValue.toFixed(toFixed));
}

export function buildRefundPayload(
  refund: RefundPayload,
  refundMetadata: RefundMetadata,
  isWithinCancellationPolicy = false,
  options?: {
    /** Currency code of the order sell currency */
    orderCurrencyCode?: string;
    /** Total vendor contribution in sell currency (including surcharge) at submit time */
    vendorContribution?: number;
    /** Vendor contribution attributed to surcharge in sell currency */
    vendorSurchargeContribution?: number;
    /** Current custom fee entered by agent for full refund with fee flow */
    currentCustomFeeForAccounting?: number;
  },
): RefundPayload {
  const cancelledWithinPolicy = convertStrOrBoolToBool(refund.mark_cancelled) && isWithinCancellationPolicy;

  const formattedAccountingMetadata = refund.accounting_metadata.map((metadata) => {
    // there's an edge case where, if you are doing a refund with fee refund
    // and no NOT mark as cancelled
    // we set the refund fee to 0

    const shouldZeroRefundFee =
      convertStrOrNumToNum(metadata.refund_fee) > 0 &&
      !convertStrOrBoolToBool(refund.mark_cancelled) &&
      !metadata.charge_component_key.includes('surcharge');

    const itemMetadata =
      !metadata.charge_component_key.includes('surcharge') && !metadata.charge_component_key.includes('merchant_fee');
    const isCurrentFullRefundWithFee =
      refund.variant_of_refund === VARIANT_OF_REFUND_ALL &&
      !convertStrOrBoolToBool(refund.mark_cancelled) &&
      itemMetadata;

    const feeToUse = isCurrentFullRefundWithFee
      ? convertStrOrNumToNum(options?.currentCustomFeeForAccounting ?? 0)
      : metadata.refund_fee;
    const refundFee = shouldZeroRefundFee ? 0 : convertStrOrNumToNum(feeToUse ?? 0);

    let accountingAmount = 0;
    let nextRefundFee = refundFee ?? 0;
    if (itemMetadata) {
      const promoAmount = getPromoAmount(refundMetadata, metadata.cash_amount);
      const customFeeForAccounting = isCurrentFullRefundWithFee
        ? convertStrOrNumToNum(options?.currentCustomFeeForAccounting ?? 0)
        : 0;
      // calculate accounting_amount based on cash_amount + promo piece
      // do not change accounting amount for customFee flow
      accountingAmount =
        metadata.refund_fee && convertStrOrBoolToBool(refund.mark_cancelled)
          ? metadata.accounting_amount
          : handleAccountingAmount(
              refundMetadata,
              metadata.cash_amount,
              metadata.accounting_amount,
              cancelledWithinPolicy,
              customFeeForAccounting,
            );
      if (cancelledWithinPolicy) {
        // Within policy: fee is strictly the accounting difference
        nextRefundFee = parseFloat((accountingAmount - metadata.cash_amount).toFixed(2));
      } else {
        // Outside policy or not cancelled: preserve custom fee and add promo piece (no accounting difference)
        nextRefundFee = parseFloat((refundFee + promoAmount).toFixed(2));
      }
    }

    return {
      ...metadata,
      accounting_amount: itemMetadata ? accountingAmount : metadata.accounting_amount,
      cash_amount: convertStrOrNumToNum(metadata.cash_amount ?? 0),
      refund_fee: nextRefundFee,
    };
  });

  refund.additional_info.version = REFUND_V2;

  const formattedAdditionalInfo: any = {
    ...refund.additional_info,
    number_of_nights: refund?.additional_info?.number_of_nights?.toString(),
  };

  // Consolidated handling of vendor_contribution and currency fields
  if (options) {
    const costCurrencyCode = refundMetadata?.package_cost_price_data?.cost_price_currency || '';
    const sellCurrencyCode = options?.orderCurrencyCode;
    const currenciesDiffer = Boolean(costCurrencyCode && sellCurrencyCode && costCurrencyCode !== sellCurrencyCode);

    // Always attach currency codes when options provided (to match previous behavior)
    formattedAdditionalInfo.cost_currency_code = costCurrencyCode;
    if (sellCurrencyCode) {
      formattedAdditionalInfo.sell_currency_code = sellCurrencyCode;
    }

    // Compute base-only vendor contribution for potential cost currency calculation
    const hasVendorContribution =
      options.vendorContribution !== undefined && Number.isFinite(options.vendorContribution);
    const basePriceVendorContribution = hasVendorContribution
      ? (options.vendorContribution ?? 0) - (options.vendorSurchargeContribution ?? 0)
      : undefined;

    let vendorContributionCostCurrency: number | undefined = undefined;
    if (currenciesDiffer && basePriceVendorContribution !== undefined) {
      const sellCostPrice = getCostPrice(refundMetadata);
      const costCostPrice = getCostPriceInCostCurrency(refundMetadata);
      const fx = sellCostPrice > 0 ? costCostPrice / sellCostPrice : undefined;
      if (fx && Number.isFinite(fx)) {
        vendorContributionCostCurrency = parseFloat((basePriceVendorContribution * fx).toFixed(2));
      }
    }

    // If cancelled within policy, vendor pays full cost price
    if (cancelledWithinPolicy) {
      formattedAdditionalInfo.vendor_contribution =
        refundMetadata.package_cost_price_data.cost_price ?? formattedAdditionalInfo.vendor_contribution;

      if (currenciesDiffer) {
        formattedAdditionalInfo.vendor_contribution_cost_currency =
          refundMetadata.package_cost_price_data.cost_price_converted_to_sell_currency ??
          vendorContributionCostCurrency;
      }
    } else {
      formattedAdditionalInfo.vendor_contribution = parseFloat(
        basePriceVendorContribution?.toFixed(2) ?? formattedAdditionalInfo.vendor_contribution ?? 0,
      );
      if (currenciesDiffer && vendorContributionCostCurrency !== undefined) {
        formattedAdditionalInfo.vendor_contribution_cost_currency = vendorContributionCostCurrency;
      }
    }
  }

  if (refund.variant_of_refund === 'partial') {
    /**
     * These `percentage` and `number_of_nights` fields are how much is payable to vendor.
     * In the new UI, we will base it around payable to customer, so we have to inverse the fields.
     */

    // the calculation for vendor_holdback = cost price - (cost price * (nights / total nights))
    // e.g.
    // if refund the customer 3/7 nights
    // we have 4 nights payable to vendor
    // so we set this field to be 4 nights.
    if (formattedAdditionalInfo.number_of_nights) {
      formattedAdditionalInfo.number_of_nights = (
        formattedAdditionalInfo.extra_nights +
        formattedAdditionalInfo.package_nights -
        parseFloat(formattedAdditionalInfo.number_of_nights)
      ).toFixed(0);
    }

    // the calculation for vendor_holdback = cost price - (cost price * percentage)
    // e.g.
    // if refund the customer 90%
    // we have 10% payable to vendor
    // so we want this field to be 10%
    if (formattedAdditionalInfo.percentage) {
      if (cancelledWithinPolicy) {
        // If the refund is marked as cancelled and is within the cancellation policy,
        // we want this field to be 0%
        formattedAdditionalInfo.percentage = '0.00000';
      } else {
        formattedAdditionalInfo.percentage = (
          100 - convertStrOrNumToNum(formattedAdditionalInfo.percentage, 5)
        ).toFixed(5);
      }
    }
  } else if (cancelledWithinPolicy) {
    // If the refund is marked as cancelled and is within the cancellation policy,
    // we want this field to be 0%
    formattedAdditionalInfo.percentage = '0.00000';
  }

  return {
    ...refund,
    send_customer_refund_notification: convertStrOrBoolToBool(refund.send_customer_refund_notification),
    mark_cancelled: convertStrOrBoolToBool(refund.mark_cancelled),
    amount: Number.isFinite(Number(refund.amount)) ? convertStrOrNumToNum(refund.amount) : 0,
    update_vcc_balance: convertStrOrBoolToBool(refund.update_vcc_balance),
    accounting_metadata: formattedAccountingMetadata,
    additional_info: refund.additional_info ? formattedAdditionalInfo : refund.additional_info,
  };
}

/**
 * @param currency AUD
 * @returns $
 */
export function getCurrencySymbol(currency) {
  return getCurrencySymbolWithLocale(currency, 'en-AU');
}

/**
 * @param currency AUD
 * @returns $
 */
export function getCurrencySymbolIntl(currency) {
  return getCurrencySymbolWithLocale(currency, 'en');
}

const currencySymbolCache: Record<string, string> = {};
function getCurrencySymbolWithLocale(currency: string, locale: string) {
  const cacheKey = `${locale}:${currency}`;
  if (currencySymbolCache[cacheKey]) {
    return currencySymbolCache[cacheKey];
  }
  const symbol =
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value || currency;
  currencySymbolCache[cacheKey] = symbol;
  return symbol;
}

export function getVendorContribution(
  refundMetadata: RefundMetadata,
  refund: RefundPayload,
  item?: App.OrderItem,
  customerRefundAmount?: string,
  selectedNights?: Set<string>,
  surchargeData?: SurchargeData,
  /**
   * When true, prefer using the stored percentage on the refund payload instead of
   * deriving the ratio from the customer refund amount. This is used on submit to
   * preserve the agent-entered custom vendor contribution
   */
  preferStoredPercentage?: boolean,
) {
  let vendorContribution = 0;
  let basePriceVendorContribution = 0;
  let surchargesVendorContribution = 0;
  const packageCostPrice = getCostPrice(refundMetadata);

  refund.accounting_metadata.forEach((accountingMetadata) => {
    const source = accountingMetadata.source;
    if (accountingMetadata.charge_component_key.includes('merchant_fee')) {
      return;
    }

    // Ensure surcharge accounting entries are not processed by base logic when there is no surcharge cost price
    if (accountingMetadata.charge_component_key.includes('surcharge')) {
      if (surchargeData?.totalSurchargeCostPrice > 0) {
        switch (source) {
          case 'Default':
          case 'Custom': {
            let ratio = 0;
            if (selectedNights?.size) {
              ratio =
                surchargeData.surchargeCostPrice > 0
                  ? surchargeData.surchargeCostPrice / Math.abs(surchargeData.totalSurchargeCostPrice)
                  : 0;
            }

            const surchargeVendorHoldbackAmount =
              surchargeData.totalSurchargeCostPrice - surchargeData.totalSurchargeCostPrice * ratio;
            surchargesVendorContribution = surchargeData.totalSurchargeCostPrice - surchargeVendorHoldbackAmount;
            vendorContribution += surchargesVendorContribution;
            break;
          }
        }
      }
      return;
    }

    switch (source) {
      case 'Default': {
        // cash amount is the amount we have for this order. Let's say order value 899, and promo amount is 199, then cash amount is 700 which is what we have.
        // similarly, let's say order value is 899, and we partially refunded 199, then cash amount is 700.
        // similarly, let's say order value is 899, and promo discount is 199, and we partially refunded 100, then cash amount is 600.
        const cashAmount = handleAccountingAmount(
          refundMetadata,
          accountingMetadata.cash_amount,
          accountingMetadata.accounting_amount,
        );
        basePriceVendorContribution = refundMetadata.total_sale_price
          ? (cashAmount / refundMetadata.total_sale_price) * packageCostPrice
          : 0;
        vendorContribution += basePriceVendorContribution;
        break;
      }
      case 'Custom': {
        let ratio = 0;
        if (refund.additional_info.refund_vendor_agrees_to === 'number') {
          ratio =
            Number(refund.additional_info.number_of_nights) /
            (refund.additional_info.package_nights + refund.additional_info.extra_nights);
        }
        if (refund.additional_info.refund_vendor_agrees_to === 'percentage' && !selectedNights?.size) {
          if (preferStoredPercentage) {
            // On submit, use the stored percentage which reflects the vendor contribution value in the form
            ratio = convertStrOrNumToNum(refund?.additional_info?.percentage ?? 0, 5) / 100;
          } else {
            // Otherwise, derive default ratio based on refund amount divided by sale price
            const packageRefundAmount = Number.isFinite(parseFloat((customerRefundAmount as any) ?? ''))
              ? parseFloat(customerRefundAmount as any)
              : convertStrOrNumToNum(refund?.accounting_metadata?.[0]?.cash_amount ?? 0);

            ratio =
              refundMetadata.total_sale_price > 0 && packageRefundAmount > 0
                ? packageRefundAmount / refundMetadata.total_sale_price
                : convertStrOrNumToNum(refund?.additional_info?.percentage ?? 0, 5) / 100;
          }
        }
        if (selectedNights?.size) {
          // use sale price as fallback for ratio calculation
          let refundAmount = parseFloat(customerRefundAmount);
          let totalAmount = refundMetadata.total_sale_price;
          // if reservationRate data is available, use it to calculate ratio (based on cost prices)
          if (surchargeData?.totalPackageCostPrice) {
            refundAmount = surchargeData.packageCostPrice;
            totalAmount = surchargeData.totalPackageCostPrice;
          }
          ratio = totalAmount ? refundAmount / totalAmount : 0;
        }
        const vendorHoldbackAmount = packageCostPrice - packageCostPrice * ratio;
        basePriceVendorContribution = packageCostPrice - vendorHoldbackAmount;
        vendorContribution += basePriceVendorContribution;
        break;
      }
      case 'TNFH': {
        // no addition to vendor contribution here
        break;
      }
    }
  });

  if (customerRefundAmount && !selectedNights?.size) {
    /**
     * We want it to be the smallest of
     * - what the agent typed in
     * - what the cost price is
     * - what the customer refund amount is
     */
    return {
      vendorContribution: Math.min(packageCostPrice, parseFloat(customerRefundAmount), vendorContribution),
      basePriceVendorContribution: Math.min(
        packageCostPrice,
        parseFloat(customerRefundAmount),
        basePriceVendorContribution,
      ),
      surchargesVendorContribution: Math.min(
        surchargeData?.surchargeCostPrice,
        parseFloat(customerRefundAmount),
        surchargesVendorContribution,
      ),
    };
  }
  return {
    vendorContribution,
    basePriceVendorContribution,
    surchargesVendorContribution,
  };
}

export function formatModalHeader(step: RefundSteps, refundMetadata: RefundMetadata): string {
  let typeOfItem,
    typeOfStep = '';
  if (refundMetadata?.is_addon) {
    typeOfItem = 'Addon price';
  } else if (refundMetadata?.is_bedbank) {
    typeOfItem = 'Room price';
  } else if (refundMetadata?.is_tour_v2) {
    typeOfItem = 'TourV2 price';
  } else if (refundMetadata?.is_deposit) {
    typeOfItem = 'Refund amount';
  } else if (refundMetadata?.is_instalment) {
    typeOfItem = 'Refund amount';
  } else if (refundMetadata?.is_pending_deferred_payment) {
    typeOfItem = 'Refund amount';
  } else {
    typeOfItem = 'Package price';
  }

  switch (step) {
    case RefundSteps.Reason: {
      typeOfStep = 'Select Type Of Refund';
      break;
    }
    case RefundSteps.Detail: {
      typeOfStep = 'Issue Refund';
      break;
    }
    case RefundSteps.Confirm: {
      typeOfStep = 'Confirm Refund';
      break;
    }
  }

  if (!refundMetadata) {
    return `${typeOfStep}`;
  }
  return `${typeOfStep} - ${typeOfItem} - ${currencyFormatter(
    refundMetadata.currency_code,
    refundMetadata.total_sale_price,
    2,
  )} ${refundMetadata.currency_code}`;
}

export function filterAccountingMetadataByChargeComponentKey(refundPayload, chargeComponentKey: string) {
  const refundPayloadCopy = { ...refundPayload };
  const refundMetaToRemove = refundPayloadCopy.accounting_metadata.find((data) =>
    data.charge_component_key.includes(chargeComponentKey),
  );
  refundPayloadCopy.amount -= refundMetaToRemove.cash_amount;
  refundPayloadCopy.accounting_metadata = refundPayloadCopy.accounting_metadata.filter(
    (data) => !data.charge_component_key.includes(chargeComponentKey),
  );
  return refundPayloadCopy;
}

interface HandleCustomRefundFeeProps {
  inputPayload: RefundPayload;
  refundMetadata: RefundMetadata;
  refundFee: string;
  markCancelled: string;
  shouldSurchargeBeRefunded: string;
}

export function handleCustomFee({
  inputPayload,
  refundFee,
  refundMetadata,
  markCancelled,
  shouldSurchargeBeRefunded,
}: HandleCustomRefundFeeProps) {
  // full refund with fee
  // Guide:
  // [0] = Item Metadata
  // [1] = Surcharge Metadata

  const updatedPayload = { ...inputPayload };
  // TODO #1 check that accounting_amount is returned if cash_amount is 0
  const updatedCashValue = refundMetadata.item_metadata.cash_amount - parseFloat(refundFee);
  updatedPayload.accounting_metadata[0].refund_fee = parseFloat(refundFee);
  /**
   * If we mark it cancelled, we want the entire amount to be taken from the order, such that vendor pays the full cost price
   * If we don't mark it cancelled, we want to leave some amount in the order and for the vendor to pay a proportional amount
   */
  if (convertStrOrBoolToBool(markCancelled)) {
    updatedPayload.accounting_metadata[0].accounting_amount = refundMetadata.item_metadata.accounting_amount;
  } else {
    updatedPayload.accounting_metadata[0].accounting_amount = updatedCashValue;
  }

  updatedPayload.accounting_metadata[0].cash_amount = updatedCashValue;
  let calculatedRefundAmount =
    refundMetadata.item_metadata.cash_amount + (refundMetadata.merchant_fee_amount ?? 0) - parseFloat(refundFee);

  if (refundMetadata.has_surcharge) {
    if (convertStrOrBoolToBool(shouldSurchargeBeRefunded)) {
      calculatedRefundAmount += refundMetadata.surcharge_metadata.cash_amount;
      // set fee and cash_amount to default values for should refund surcharge
      updatedPayload.accounting_metadata[1].cash_amount = refundMetadata.surcharge_metadata.cash_amount;
      updatedPayload.accounting_metadata[1].refund_fee = 0;
    } else {
      // we keep the surcharge accounting metadata, but charge the full surcharge
      // amount as the refund fee for the mark cancelled case

      updatedPayload.accounting_metadata[1].refund_fee = refundMetadata.surcharge_metadata.cash_amount;
      updatedPayload.accounting_metadata[1].cash_amount = 0;
    }
  }

  updatedPayload.amount = parseFloat(calculatedRefundAmount.toFixed(2));

  updatedPayload.variant_of_refund = VARIANT_OF_REFUND_ALL;

  // unlikely these fields are used, keeping for posterity to match previous implementation
  updatedPayload.additional_info.refund_vendor_agrees_to = VendorRefundTypes.number;
  updatedPayload.additional_info.number_of_nights = refundMetadata.total_nights;

  return updatedPayload;
}

export function getPromoPiece(refundMetadata: RefundMetadata, cashAmount: number) {
  if (cashAmount === 0) {
    return 0;
  }

  const promoPiece =
    refundMetadata.item_metadata.promo_percentage * (refundMetadata.item_metadata.initial_amount / cashAmount);
  return promoPiece;
}

export function getPromoAmount(refundMetadata: RefundMetadata, cashAmount: number) {
  const itemMetadata = refundMetadata.item_metadata;
  if (cashAmount === 0 || !itemMetadata.include_promo) {
    return 0;
  }
  // const promoPiece = itemMetadata.promo_percentage * cashAmount;
  const totalPromoAmount = itemMetadata.initial_amount * itemMetadata.promo_percentage;
  return (totalPromoAmount / itemMetadata.cash_amount_original) * cashAmount;
}

export function handleAccountingAmount(
  refundMetadata: RefundMetadata,
  refundAmountToCustomer: number,
  accountingAmountInput: number,
  useFullAccountingAmount = false,
  customFee = 0,
) {
  let cashAmount = refundAmountToCustomer ? refundAmountToCustomer ?? 0 : 0;
  if (cashAmount === 0 || refundMetadata.is_pending_deferred_payment) {
    return accountingAmountInput;
  }

  if (useFullAccountingAmount) {
    return refundMetadata.item_metadata.accounting_amount;
  }

  if (cashAmount !== 0) {
    const promoPiece = getPromoPiece(refundMetadata, cashAmount);
    if (refundMetadata.item_metadata.include_promo) {
      const promoAmount = parseFloat(`${promoPiece * cashAmount}`);
      cashAmount =
        (refundMetadata.item_metadata.initial_amount / (refundMetadata.item_metadata.initial_amount - promoAmount)) *
        cashAmount;
    }

    if (customFee) {
      return cashAmount + customFee;
    }

    return cashAmount;
  }

  return accountingAmountInput;
}

interface HandleVendorPercentageProps {
  inputPayload: RefundPayload;
  percentageRefundToCustomer: string;
  customVendorContribution: string;
  refundMetadata: RefundMetadata;
  shouldSurchargeBeRefunded?: string;
  markCancelled?: string;
}
export function handleVendorPercentage({
  inputPayload,
  percentageRefundToCustomer,
  customVendorContribution,
  refundMetadata,
}: HandleVendorPercentageProps) {
  const updatedPayload = { ...inputPayload };
  // select percentage
  // If sell currency and cost currency differ, compute percentage in cost currency
  const costCurrency = refundMetadata?.package_cost_price_data?.cost_price_currency;
  const sellCurrency = refundMetadata?.currency_code;
  const currenciesDiffer = Boolean(costCurrency && sellCurrency && costCurrency !== sellCurrency);
  const costPrice = currenciesDiffer ? getCostPriceInCostCurrency(refundMetadata) : getCostPrice(refundMetadata);
  updatedPayload.additional_info.refund_vendor_agrees_to = VendorRefundTypes.percentage;
  const safePercentageRefundToCustomer = Number.isFinite(parseFloat(percentageRefundToCustomer as any))
    ? parseFloat(percentageRefundToCustomer as any)
    : 0;
  updatedPayload.amount = safePercentageRefundToCustomer;
  updatedPayload.accounting_metadata[0].accounting_amount = safePercentageRefundToCustomer;
  updatedPayload.accounting_metadata[0].cash_amount = safePercentageRefundToCustomer;

  if (customVendorContribution) {
    const safeCustomVendorContribution = Number.isFinite(parseFloat(customVendorContribution as any))
      ? parseFloat(customVendorContribution as any)
      : 0;
    updatedPayload.additional_info.percentage = ((safeCustomVendorContribution / costPrice) * 100).toFixed(5);
    updatedPayload.additional_info.vendor_contribution = safeCustomVendorContribution;
  } else {
    // Preserve existing percentage if previously set; otherwise default to 100%
    updatedPayload.additional_info.percentage = '100';
  }
  // unused, keeping to match existing payload
  updatedPayload.additional_info.number_of_nights = refundMetadata.total_nights;
  return updatedPayload;
}

interface HandleSelectNightsProps {
  inputPayload: RefundPayload;
  totalRefundedToCustomer: string;
  numberOfNights: string;
  surchargeRefundedToCustomer?: string;
  selectedNights?: Set<string>;
}

export function handleSelectNights({
  inputPayload,
  totalRefundedToCustomer,
  numberOfNights,
  surchargeRefundedToCustomer = '0',
  selectedNights,
}: HandleSelectNightsProps) {
  const updatedPayload = { ...inputPayload };
  updatedPayload.additional_info.refund_vendor_agrees_to = selectedNights?.size
    ? VendorRefundTypes.percentage
    : VendorRefundTypes.number;

  // Handle package refund
  const safeTotalRefundedToCustomer = Number.isFinite(parseFloat((totalRefundedToCustomer as any) ?? ''))
    ? parseFloat(totalRefundedToCustomer as any)
    : 0;
  const safeSurchargeRefundedToCustomer = Number.isFinite(parseFloat((surchargeRefundedToCustomer as any) ?? ''))
    ? parseFloat(surchargeRefundedToCustomer as any)
    : 0;
  updatedPayload.amount = safeTotalRefundedToCustomer + safeSurchargeRefundedToCustomer;
  updatedPayload.accounting_metadata[0].accounting_amount = safeTotalRefundedToCustomer;
  updatedPayload.accounting_metadata[0].cash_amount = safeTotalRefundedToCustomer;
  updatedPayload.additional_info.number_of_nights = numberOfNights ?? '0';

  if (surchargeRefundedToCustomer && safeSurchargeRefundedToCustomer > 0 && updatedPayload.accounting_metadata[1]) {
    // Handle surcharge refund
    updatedPayload.accounting_metadata[1].cash_amount = safeSurchargeRefundedToCustomer;
    updatedPayload.accounting_metadata[1].accounting_amount = safeSurchargeRefundedToCustomer;
  }

  return updatedPayload;
}

export function getCostPrice(refundMetadata: RefundMetadata) {
  const convertedPackageCostPrice =
    refundMetadata.package_cost_price_data?.cost_price_converted_to_sell_currency &&
    convertStrOrNumToNum(refundMetadata.package_cost_price_data.cost_price_converted_to_sell_currency);

  const packageCostPrice =
    refundMetadata.package_cost_price_data?.cost_price &&
    convertStrOrNumToNum(refundMetadata.package_cost_price_data.cost_price);

  let convertedExtraNightsCostPrice = 0;
  let extraNightsCostPrice = 0;

  if (convertStrOrNumToNum(refundMetadata.extra_nights) > 0) {
    // Extra nights pricing is per night unlike packages which is the total
    convertedExtraNightsCostPrice =
      refundMetadata.extra_nights_cost_price_data?.cost_price_converted_to_sell_currency &&
      convertStrOrNumToNum(refundMetadata.extra_nights_cost_price_data.cost_price_converted_to_sell_currency);

    extraNightsCostPrice =
      refundMetadata.extra_nights_cost_price_data?.cost_price &&
      convertStrOrNumToNum(refundMetadata.extra_nights_cost_price_data.cost_price);
  }

  const fullCostPrice =
    (convertedPackageCostPrice ?? packageCostPrice) +
    (convertedExtraNightsCostPrice ?? extraNightsCostPrice) * convertStrOrNumToNum(refundMetadata.extra_nights);
  return parseFloat(fullCostPrice.toFixed(2));
}

/**
 * Returns total cost price strictly in the original cost currency (no conversion to sell currency).
 */
export function getCostPriceInCostCurrency(refundMetadata: RefundMetadata) {
  const packageCostPrice =
    refundMetadata.package_cost_price_data?.cost_price &&
    convertStrOrNumToNum(refundMetadata.package_cost_price_data.cost_price);

  let extraNightsCostPrice = 0;
  if (convertStrOrNumToNum(refundMetadata.extra_nights) > 0) {
    extraNightsCostPrice =
      refundMetadata.extra_nights_cost_price_data?.cost_price &&
      convertStrOrNumToNum(refundMetadata.extra_nights_cost_price_data.cost_price);
  }

  const fullCostPrice =
    (packageCostPrice ?? 0) + extraNightsCostPrice * convertStrOrNumToNum(refundMetadata.extra_nights);
  return parseFloat(fullCostPrice.toFixed(2));
}

export function handleSelectNightsAdditionalInfo(
  vendorContribution: number,
  refundMetadata: RefundMetadata,
  selectedNights: Set<string>,
  surchargeData?: SurchargeData,
) {
  const costPrice = getCostPrice(refundMetadata);

  // Base additional info (for non-surcharge refund)
  const baseAdditionalInfo: any = {
    percentage: (
      (parseFloat((vendorContribution - (surchargeData?.surchargeCostPrice ?? 0)).toFixed(2)) / costPrice) *
      100
    ).toFixed(5),
    vendor_contribution: parseFloat((vendorContribution - (surchargeData?.surchargeCostPrice ?? 0)).toFixed(2)),
  };

  // Surcharge additional info (for surcharge refund)
  let surchargeAdditionalInfo = null;

  if (surchargeData && surchargeData.totalSurchargeCostPrice > 0) {
    let ratio = 0;

    if (surchargeData.surchargeCostPrice > 0 && surchargeData.totalSurchargeCostPrice > 0) {
      ratio = surchargeData.surchargeCostPrice / surchargeData.totalSurchargeCostPrice;
    }

    const surchargeVendorHoldbackAmount =
      surchargeData.totalSurchargeCostPrice - surchargeData.totalSurchargeCostPrice * ratio;
    const surchargeVendorContribution = surchargeData.totalSurchargeCostPrice - surchargeVendorHoldbackAmount;

    // Calculate surcharge percentage based on vendor contribution and total cost price
    const surchargePercentage =
      surchargeData.totalSurchargeCostPrice > 0
        ? ((surchargeVendorContribution / surchargeData.totalSurchargeCostPrice) * 100).toFixed(5)
        : '0';

    surchargeAdditionalInfo = {
      percentage: (100 - convertStrOrNumToNum(surchargePercentage, 5)).toFixed(5),
      vendor_contribution: parseFloat(surchargeVendorContribution.toFixed(2)),
    };
  }

  return { baseAdditionalInfo, surchargeAdditionalInfo };
}

export function getSelectNightsPricesByDay(reservationRates: Array<ReservationRate>) {
  const priceMap: { [key: string]: NightPrices } = {};
  const surchargesMap: SurchargePrices = {
    extra_guest_surcharge_sell_amount: 0,
    peak_period_surcharge_sell_amount: 0,
    surcharge_internal_sell_amount: 0,
  };

  reservationRates.forEach((rate) => {
    const dateKey = new Date(rate.date).toISOString();
    const surcharges: SurchargePrices = {
      extra_guest_surcharge_sell_amount: rate.extra_guest_surcharge_sell_amount || 0,
      peak_period_surcharge_sell_amount: rate.peak_period_surcharge_sell_amount || 0,
      surcharge_internal_sell_amount: rate.surcharge_internal_sell_amount || 0,
    };

    const totalSurcharge =
      surcharges.extra_guest_surcharge_sell_amount +
      surcharges.peak_period_surcharge_sell_amount +
      surcharges.surcharge_internal_sell_amount;

    priceMap[dateKey] = {
      packagePrice: rate.sell_amount,
      surcharges,
      totalSurcharge,
    };

    surchargesMap.extra_guest_surcharge_sell_amount += surcharges.extra_guest_surcharge_sell_amount;
    surchargesMap.peak_period_surcharge_sell_amount += surcharges.peak_period_surcharge_sell_amount;
    surchargesMap.surcharge_internal_sell_amount += surcharges.surcharge_internal_sell_amount;
  });

  return {
    priceMap,
    surchargesMap,
  };
}

export function buildSelectNightsSurchargeRefundPayload(
  baseRefund: RefundPayload,
  surchargeMetadata: RefundPayloadAccountingMetadata,
  surchargeAdditionalInfo: Partial<AdditionalInfo>,
  shouldAllowCancel,
): RefundPayload {
  return {
    ...baseRefund,
    transaction_key: uuidv4(),
    accounting_metadata: [surchargeMetadata],
    amount: surchargeMetadata.cash_amount,
    mark_cancelled: shouldAllowCancel ? baseRefund.mark_cancelled : false,
    additional_info: {
      ...baseRefund.additional_info,
      ...(surchargeAdditionalInfo || {}),
    },
  };
}

export function buildSelectNightsNonSurchargeRefundPayload(
  baseRefund: RefundPayload,
  nonSurchargeMetadata: Array<RefundPayloadAccountingMetadata>,
  totalAmount: number,
  surchargeAmount = 0,
): RefundPayload {
  return {
    ...baseRefund,
    transaction_key: uuidv4(),
    accounting_metadata: nonSurchargeMetadata,
    amount: parseFloat((totalAmount - surchargeAmount).toFixed(2)),
  };
}

export function buildMerchandiseRefundPayload(
  merchandiseMetadata: Array<AccountingMetaPayload>,
  options: {
    reason: string;
    refundMethod: string;
    ticketId: string;
    sendRefundNotification: boolean;
    currencyCode: string;
    amount: number;
    comment: string;
    merchant_fee_amount?: number;
    additional_info?: {
      retain_margin?: boolean;
      includes_shipping_refund?: boolean;
    };
  },
) {
  const {
    reason,
    refundMethod,
    ticketId,
    sendRefundNotification,
    amount,
    currencyCode,
    comment,
    merchant_fee_amount,
    additional_info,
  } = options;

  // to fix where the source is not set for Delivery Fee
  const formattedMetadata = merchandiseMetadata.map((metadata) => ({
    ...metadata,
    source: metadata.source || 'Default',
  }));

  const payload = {
    transaction_key: uuidv4(),
    reason,
    refund_method: refundMethod,
    comment,
    ticket_id: ticketId,
    send_refund_notification: sendRefundNotification,
    accounting_metadata: formattedMetadata,
    amount,
    currency_code: currencyCode,
    mark_cancelled: true,
    merchant_fee_amount,
    ...(additional_info && { additional_info }),
  };

  return payload;
}
